import type { SharedData } from '@/types';

type FreshdeskWidgetConfig = {
    id: string;
    locale: string;
    excludedPaths: string[];
};

type WidgetIdentify = {
    name?: string;
    email: string;
};

type PageWithAuth = {
    props: SharedData & {
        businessUser?: { name?: string; email?: string } | null;
    };
};

function widgetConfig(): FreshdeskWidgetConfig | null {
    return window.freshdeskWidget ?? null;
}

function pathMatches(pathname: string, pattern: string): boolean {
    const path = pathname.replace(/^\//, '');

    if (pattern.endsWith('/*')) {
        const prefix = pattern.slice(0, -2);

        return path === prefix || path.startsWith(`${prefix}/`);
    }

    return path === pattern;
}

function isExcludedPath(pathname: string, excludedPaths: string[]): boolean {
    return excludedPaths.some((pattern) => pathMatches(pathname, pattern));
}

function identifyFromPage(page: PageWithAuth): WidgetIdentify | null {
    const user = page.props.auth?.user ?? page.props.businessUser;

    if (!user?.email) {
        return null;
    }

    return {
        name: user.name,
        email: user.email,
    };
}

function callWidget(...args: unknown[]): void {
    window.FreshworksWidget?.(...args);
}

let identified = false;

function syncIdentity(page: PageWithAuth): void {
    const identity = identifyFromPage(page);

    if (identity) {
        callWidget('identify', 'ticketForm', identity);
        identified = true;

        return;
    }

    if (identified) {
        callWidget('logout');
        identified = false;
    }
}

export function syncFreshdeskWidget(page: PageWithAuth): void {
    const config = widgetConfig();

    if (!config?.id || typeof window.FreshworksWidget !== 'function') {
        return;
    }

    if (isExcludedPath(window.location.pathname, config.excludedPaths)) {
        callWidget('hide', 'launcher');

        return;
    }

    callWidget('show', 'launcher');
    syncIdentity(page);
}

export function initializeFreshdeskWidget(page: PageWithAuth): void {
    identified = identifyFromPage(page) !== null;
    syncFreshdeskWidget(page);
}
